import React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { ProcessesTaskConfigurationSignatureDocument } from 'generated/resources/graphql'
import moment from 'moment'

import DocumentType from 'modules/core/components/DocumentType'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { formatTime } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'

import SignatureStatusTag from 'modules/documents/components/Documents/List/Item/SignatureStatusTag'
import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import { usePreviewProvider } from 'modules/processes/components/ItemWizard/Providers/preview'
import {
  File,
  buildFiles,
} from 'modules/processes/components/ItemWizard/components/Preview/Attachments'
import { TaskConfigurationTypes } from 'modules/processes/lib/taskConfigurationTypes'

const DocumentToComplete = () => {
  const { previewValues } = usePreviewProvider()
  const { form } = useFormProvider()
  const currentAccess = useCurrentSession().access

  const files = buildFiles(
    previewValues?.signDocument?.filesToAdd,
    previewValues?.signDocument?.filesToRemove,
    previewValues?.signDocument?.currentFiles
  )

  const documents =
    form.get('additional_form_data')?.value[TaskConfigurationTypes.SignDocuments]
      ?.signatureDocuments.filesToAdd ??
    previewValues?.signDocument?.currentFiles ??
    []
  const isSignDocument = form.get('task_type')?.value === TaskConfigurationTypes.SignDocuments

  const formattedDatetime = () => {
    const date = moment()
    const formattedDate = date.format('ll')
    const formattedTime = formatTime(currentAccess, date.format('HH:mm'))

    return `${formattedDate}, ${formattedTime}`
  }

  const documentBox = (documentName: string) => {
    return (
      <Box
        width="full"
        flexDirection="row"
        padding="s16"
        alignItems="center"
        justifyContent="spaceBetween"
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
        borderRadius={{
          topLeft: 'abs008',
          topRight: 'abs008',
          bottomLeft: 'abs008',
          bottomRight: 'abs008',
        }}
      >
        <Box flexDirection="row" justifyContent="spaceBetween" gap="s16" width="full">
          <Box flexDirection="row" alignItems="center" gap="s16">
            <DocumentType mimetype={'pdf'} />
            <Box minWidth="none" maxWidth="s360">
              <Text.Normal weight="medium" ellipsis>
                {documentName}
              </Text.Normal>
              <Box paddingTop="s4">
                <Text.Normal size="200" color="grey800" ellipsis>
                  {formattedDatetime()}
                  {` - ${i18n.t('documents.uploaded_by', {
                    authorName: '?',
                  })}`}
                </Text.Normal>
              </Box>
            </Box>
          </Box>
          <Box flexDirection="row" alignItems="center">
            <Box paddingX="s8">
              <SignatureStatusTag status={'pending'} />
            </Box>

            <Box paddingRight="s8">
              <Button type="secondary" iconName="ellipsis" />
            </Box>

            <Button type="secondary" iconName="arrow-right" />
          </Box>
        </Box>
      </Box>
    )
  }

  const documentHtml = (
    <Box>
      <Box paddingY="s12">
        <Text.Normal weight="semibold" color="grey1000">
          {i18n.t(
            'processes.process_settings.wizard.tasks.types.sign_documents.preview.documents_to_complete.title'
          )}
        </Text.Normal>
      </Box>
      <Box paddingBottom="s12">
        <Text.Normal weight="normal" color="grey800">
          {i18n.t(
            'processes.process_settings.wizard.tasks.types.sign_documents.preview.documents_to_complete.description',
            { folder_name: previewValues.name }
          )}
        </Text.Normal>
      </Box>
    </Box>
  )

  if (previewValues.documentTemplateName) {
    return (
      <>
        {documentHtml}
        {isSignDocument
          ? files.map((file, index) => <File key={index} file={file} />)
          : documentBox(previewValues.documentTemplateName)}
      </>
    )
  }

  return documents.length ? (
    <>
      {documentHtml}
      {documents.map((document: File & ProcessesTaskConfigurationSignatureDocument) =>
        documentBox(document?.name || document['filename'])
      )}
    </>
  ) : null
}

export default DocumentToComplete
